import _extends from "@babel/runtime/helpers/extends";

/**
 * Fetch Supplemental Terms
 *
 * @param {String} apiIxvocabAuth
 * @param {String} apiIxvocabUrl
 * @param {String} ixVocabContextId
 *   The ID of the vocab context we'll be getting from the API (provided as
 *   metadata from the product).
 * @returns {Promise<Object[]>} - Array of objects representing supplemental policy data
 *          {string}   Promise<Object[].label>
 *          {Object[]} Promise<Object[].terms>
 *          {string}   Promise<Object[].terms[].dataType>
 *          {string}   Promise<Object[].terms[].description>
 *          {Object}   Promise<Object[].terms[].enumerations>
 *          {string}   Promise<Object[].terms[].label>
 *          {Object[]} Promise<Object[].terms[].meta>
 *          {Object[]} Promise<Object[].terms[].name>
 *
 */
export default function getSupplementalPolicyTerms(_ref) {
  var apiIxvocabAuth = _ref.apiIxvocabAuth,
      apiIxvocabUrl = _ref.apiIxvocabUrl,
      ixVocabContextId = _ref.ixVocabContextId;
  return new Promise(function (resolve) {
    fetch(apiIxvocabUrl + "/contexts/" + ixVocabContextId + "/terms", _extends({
      method: "GET"
    }, apiIxvocabAuth ? {} : {
      credentials: "include"
    }, {
      headers: _extends({
        Accept: "application/json"
      }, apiIxvocabAuth ? {
        Authorization: "Basic " + apiIxvocabAuth
      } : {})
    })).then(function (res) {
      return res.ok ? res.json() : [];
    }).then(function (json) {
      // Ensure that the response we got is what we're expecting
      if (!(json.length > 0 && json[0].label)) {
        // eslint-disable-next-line no-console
        console.error("ixvocab returned unexpected content.");
        resolve();
      }

      resolve(parseIxvocabResponse(json));
    }).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load supplemental policy terms.", err);
      resolve([]);
    });
  });
} // Here we will massage the data coming back from the server so it conforms
// to the shape that we need for the UI component.

export function parseIxvocabResponse(response) {
  return response // Get a list of categories, i.e. ['Category1', 'Category2']
  .reduce(function (acc, term) {
    var category = term.meta.find(function (meta) {
      return meta.name === "Category";
    }).value;
    return acc.indexOf(category) !== -1 ? acc : acc.concat(category);
  }, []) // Sort them alphabetically (as no order is defined by the server)
  .sort() // Return all terms grouped by category, i.e.
  // [
  //   { label: 'Category1', terms: [{...}] }
  //   { label: 'Category2', terms: [{...}] }
  // ]
  .map(function (category) {
    return {
      label: category,
      terms: response // Get only the terms of the current category
      .filter(function (term) {
        return term.meta.find(function (meta) {
          return meta.name === "Category" && meta.value === category;
        });
      }) // Sort by the meta sequence value
      // Most likely the sequence values will be numbers as strings
      //   i.e. "1", "2"
      // But in some cases, they may be decimals as strings
      //   i.e. "1.1", "1.2"
      // So we will convert the string to a float so we can properly sort the
      // terms as defined in ixvocab.
      .sort(function (a, b) {
        var aSequence = parseFloat(a.meta.find(function (meta) {
          return meta.name === "Sequence";
        }).value);
        var bSequence = parseFloat(b.meta.find(function (meta) {
          return meta.name === "Sequence";
        }).value);
        return aSequence < bSequence ? -1 : aSequence > bSequence ? 1 : 0;
      }) // Return all data with any special pieces we want massaged
      .map(function (term) {
        var displayFormat = term.meta.find(function (meta) {
          return meta.name === "DisplayFormat";
        });
        return _extends({}, term, {
          // If there is a "Display Format" in the meta, that overrides the
          // `dataType` in telling us how we should display the value.
          dataType: displayFormat ? displayFormat.value.toLowerCase() : term.dataType.toLowerCase(),
          // Flatten the array of enumerations (if present) into an object of
          // key/value pairs
          enumerations: term.enumerations.enumerations ? term.enumerations.enumerations.reduce(function (acc, enm) {
            var _extends2;

            return _extends({}, acc, (_extends2 = {}, _extends2[enm.value] = enm.label, _extends2));
          }, {}) : {}
        });
      })
    };
  });
}